import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import EmojiPicker from "emoji-picker-react";
import GiphySearchBox from "react-giphy-searchbox";
import "./Communities.css";

import Post from "./Post";
import ReplyDialog from "./ReplyDialog";
import EditPostModal from "./EditPostModal";
import PostDetailModal from "./PostDetailModal";

// Keep these in sync with backend validation list + Home selector list
const CATEGORIES = [
  "General",
  "AI",
  "Crypto",
  "Economy",
  "Entertainment",
  "Finance",
  "Health",
  "Media",
  "News",
  "Politics",
  "Science",
  "Sports",
  "Technology",
  "Travel",
  "Weather",
  "World",
];

const COMMUNITY_OVERVIEWS = {
  General:
    "Conversations that don't fit neatly into another community. Share ideas, observations, questions and topics of general interest.",
  Politics:
    "Discuss governments, elections, public policy and political developments. Exchange different perspectives on the decisions shaping society.",
  Economy:
    "Explore business, markets, jobs, trade, personal finance and economic trends affecting people and communities around the world.",
  Technology:
    "Discuss technology, computing, software, devices and digital innovation, and how technological change is affecting everyday life.",
  AI:
    "Explore artificial intelligence, emerging AI technologies, applications and their impact on work, society, creativity and the future.",
  Health:
    "Discuss health, wellness, healthcare and the issues affecting individual and public health.",
  Science:
    "Explore discoveries, research, space, the environment and scientific developments that expand our understanding of the world.",
  Sports:
    "Follow teams, athletes, competitions and the stories surrounding sports locally and around the world.",
  Entertainment:
    "Discuss movies, television, music, celebrities, gaming and the entertainment people are watching, hearing and talking about.",
  World:
    "Discuss international events, countries, cultures and developments that cross borders and shape the wider world.",
  News:
    "Share and discuss current events and breaking stories from local, national and international sources.",
  Media:
    "Discuss journalism, social media, communications and how information is created, distributed and understood.",
  Crypto: "Discuss cryptocurrencies, blockchain, digital assets, stablecoins, payments and the technologies shaping decentralized finance. Share news, analysis, opportunities and criticism from across the crypto world.",
  Finance: "Discuss personal finance, investing, banking, saving, borrowing and financial decisions that affect everyday life.",
  Travel: "Explore destinations, tourism, airlines, accommodations, travel experiences and the issues shaping how we explore the world.",
  Weather: "Discuss forecasts, storms, extreme weather, climate-related impacts and how changing conditions affect communities and daily life.",
};

function safeJsonParse(val) {
  try {
    return JSON.parse(val);
  } catch {
    return null;
  }
}

const getCategoryLabel = (category) => category;

function getRequestedCategoryFromUrl() {
  try {
    const params = new URLSearchParams(window.location.search || "");
    const requested = params.get("category");
    return CATEGORIES.includes(requested) ? requested : "General";
  } catch {
    return "General";
  }
}

export default function Communities() {
  const [activeCategory, setActiveCategory] = useState(() => getRequestedCategoryFromUrl());
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [communityPickerOpen, setCommunityPickerOpen] = useState(false);

  // ---- New post composer (Communities-scoped) ----
  const [showComposer, setShowComposer] = useState(false);
  const [postContent, setPostContent] = useState("");
  const [selectedFile, setSelectedFile] = useState(null);
  const [selectedGif, setSelectedGif] = useState(null);
  const [showGifPicker, setShowGifPicker] = useState(false);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);

  // File input ref (so we can clear it + allow selecting same file again)
  const fileInputRef = useRef(null);

  // Dedicated Communities scroll owner
  const contentScrollRef = useRef(null);

  // Reply dialog state (match Home usage)
  const [showReplyDialog, setShowReplyDialog] = useState(false);
  const [currentReplyPost, setCurrentReplyPost] = useState(null);

  // Edit modal state (match Home usage)
  const [editingPost, setEditingPost] = useState(null);

  // Show-more modal (match Home)
  const [showPostModal, setShowPostModal] = useState(false);
  const [selectedPost, setSelectedPost] = useState(null);

  const currentUser = useMemo(() => {
    const raw = localStorage.getItem("currentUser");
    return raw ? safeJsonParse(raw) : null;
  }, []);

  const API = process.env.REACT_APP_API_URL || "";

  const toAbs = (maybePath) => {
    if (!maybePath) return "";
    if (String(maybePath).startsWith("http")) return maybePath;
    return `${API}${maybePath.startsWith("/") ? "" : "/"}${maybePath}`;
  };

  const scrollCommunitiesContentToTop = useCallback(() => {
    if (contentScrollRef.current) {
      contentScrollRef.current.scrollTop = 0;
    }
  }, []);

  const openReplyDialog = (postId) => {
    if (!posts || posts.length === 0) return;
    const postToReply = posts.find((p) => String(p.id) === String(postId));
    if (!postToReply) return;
    setCurrentReplyPost(postToReply);
    setShowReplyDialog(true);
  };

  const handleProfileNavigation = (username) => {
    window.location.href = `/profile/${encodeURIComponent(username)}`;
  };

  // Show-more modal handler (same idea as Home)
  const handleShowModal = (post) => {
    if (!post) return;

    const fullMediaUrl = post.media_url ? toAbs(post.media_url) : null;
    const fullGifUrl = post.gif_url ? post.gif_url : null;

    // Ensure thumbnail absolute for PostDetailModal
    const fullThumbnail = post.thumbnail ? toAbs(post.thumbnail) : "/images/default-thumbnail.jpg";

    setSelectedPost({
      ...post,
      media_url: fullMediaUrl,
      gif_url: fullGifUrl,
      thumbnail: fullThumbnail,
    });

    setShowPostModal(true);
  };

  const handleLike = async (postId) => {
    try {
      if (!currentUser?.username) return;

      setPosts((prev) =>
        prev.map((p) => (p.id === postId ? { ...p, likes: (p.likes || 0) + 1 } : p))
      );

      const response = await fetch(`${API}/posts/${postId}/like`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({}),
      });

      const contentType = response.headers.get("content-type");
      if (!contentType || !contentType.includes("application/json")) return;

      const data = await response.json().catch(() => null);

      if (data?.success) {
        const likeCountResponse = await fetch(`${API}/posts/${postId}`);
        const likeData = await likeCountResponse.json().catch(() => ({}));
        setPosts((prev) =>
          prev.map((p) => (p.id === postId ? { ...p, likes: likeData.likes } : p))
        );
      }
    } catch {
      // silent
    }
  };

  const handleRepost = async (postId) => {
    try {
      if (!currentUser?.username) return;

      const response = await fetch(`${API}/posts/${postId}/repost`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({}),
      });

      if (!response.ok) return;

      await response.json().catch(() => null);

      setPosts((prev) =>
        prev.map((p) =>
          p.id === postId ? { ...p, repostsCount: (p.repostsCount || 0) + 1 } : p
        )
      );
    } catch {
      // silent
    }
  };

  const handleReply = async (postId, replyData) => {
    const content =
      typeof replyData === "string" ? replyData : replyData?.content || "";
    const file =
      typeof replyData === "object" ? replyData?.file || null : null;
    const gif =
      typeof replyData === "object" ? replyData?.gif || null : null;

    if (!content.trim() && !file && !gif) return;

    try {
      if (!currentUser?.username) return;

      const formData = new FormData();
      formData.append("content", content);

      if (file) formData.append("file", file);
      if (gif) formData.append("gif", gif);

      const response = await fetch(`${API}/posts/${postId}/reply`, {
        method: "POST",
        credentials: "include",
        body: formData,
      });

      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.error || "Failed to reply to post.");
      }

      setShowReplyDialog(false);
      await fetchCategory(activeCategory);
    } catch (err) {
      console.error("Reply failed:", err);
      throw err;
    }
  };

  const handleDelete = async (postId, userObj) => {
    if (!userObj?.username) return;
    if (!window.confirm("Are you sure you want to delete this post?")) return;

    try {
      const response = await fetch(`${API}/posts/${postId}`, {
        method: "DELETE",
        credentials: "include",
      });

      if (!response.ok) return;
      setPosts((prev) => prev.filter((p) => p.id !== postId));
    } catch {
      // silent
    }
  };

  const canEditPost = (u, post) => {
    if (!u || !post) return false;

    const minutesPassed = (Date.now() - new Date(post.createdAt)) / 60000;
    const isOwner = u.id === post.user_id;

    if (u.group_id === 1) return isOwner;
    if (u.group_id >= 2 && u.group_id <= 4) return isOwner && minutesPassed <= 60;
    if (u.group_id === 6) return isOwner && minutesPassed <= 30;
    if (u.group_id === 7) return isOwner && minutesPassed <= 45;
    if (u.group_id === 8) return isOwner && minutesPassed <= 60;
    if (u.group_id === 9) return isOwner;

    return false;
  };

  const requestBoostHandler = async (postId, btn) => {
    try {
      const viewer = currentUser;
      const uid = viewer?.id;
      if (!uid) {
        alert("Please log in.");
        return;
      }

      btn.disabled = true;
      btn.textContent = "Working...";

      const res = await fetch(`${API}/boost/request`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ userId: uid, postId }),
      });

      const data = await res.json().catch(() => ({}));

      if (res.ok) {
        btn.textContent = "Requested";
      } else if (res.status === 409) {
        btn.textContent = "Already Requested";
      } else if (res.status === 429) {
        btn.textContent = "Limit Reached";
      } else {
        btn.disabled = false;
        btn.textContent = "Boost";
        alert(data?.message || "Request failed.");
      }
    } catch {
      btn.disabled = false;
      btn.textContent = "Boost";
      alert("Network error.");
    }
  };

  const fetchCategory = useCallback(
    async (category) => {
      setLoading(true);
      setErr("");

      try {
        const endpoint = `${API}/posts/category/${encodeURIComponent(category)}`;

        const response = await fetch(endpoint, { credentials: "include" });
        if (!response.ok) throw new Error("Failed to fetch posts.");

        const data = await response.json().catch(() => []);
        const list = Array.isArray(data) ? data : [];

        const updatedPosts = list.map((post) => ({
          ...post,
          repliesCount: Number(post.repliesCount ?? 0),
          repostsCount: Number(post.repostsCount ?? 0),
          likes: Number(post.likes ?? 0),
        }));

        setPosts(updatedPosts);
      } catch (e) {
        setPosts([]);
        setErr(e?.message || "Error loading posts");
      } finally {
        setLoading(false);
      }
    },
    [API, currentUser?.id]
  );

  useEffect(() => {
    // Notify the shared desktop Header whenever the selected community changes.
    window.dispatchEvent(
      new CustomEvent("scoldyCommunityChanged", {
        detail: { category: activeCategory },
      })
    );
  }, [activeCategory]);

  useEffect(() => {
    const requested = getRequestedCategoryFromUrl();
    if (requested !== activeCategory) {
      setActiveCategory(requested);
      return;
    }

    fetchCategory(activeCategory);

    // close composer whenever user changes category
    setShowComposer(false);
    setPostContent("");
    setSelectedFile(null);
    setSelectedGif(null);
    setShowGifPicker(false);
    setShowEmojiPicker(false);
    setSearchQuery("");

    if (fileInputRef.current) fileInputRef.current.value = "";

    requestAnimationFrame(() => {
      scrollCommunitiesContentToTop();
    });
  }, [activeCategory, fetchCategory, scrollCommunitiesContentToTop]);

  useEffect(() => {
    const handleResetCommunities = () => {
      setShowComposer(false);
      setPostContent("");
      setSelectedFile(null);
      setSelectedGif(null);
    setShowGifPicker(false);
    setShowEmojiPicker(false);
      setSearchQuery("");
      if (fileInputRef.current) fileInputRef.current.value = "";

      if (activeCategory !== "General") {
        setActiveCategory("General");
      }

      try {
        const url = new URL(window.location.href);
        url.searchParams.delete("category");
        window.history.replaceState(
          {},
          "",
          `${url.pathname}${url.searchParams.toString() ? `?${url.searchParams.toString()}` : ""}`
        );
      } catch {}

      requestAnimationFrame(() => {
        scrollCommunitiesContentToTop();
      });

      setTimeout(() => {
        scrollCommunitiesContentToTop();
      }, 50);
    };

    window.addEventListener("resetCommunitiesToGeneral", handleResetCommunities);

    return () => {
      window.removeEventListener("resetCommunitiesToGeneral", handleResetCommunities);
    };
  }, [activeCategory, scrollCommunitiesContentToTop]);

  const handleCommunityMediaUpload = (event) => {
    const file = event.target.files?.[0] || null;
    if (!file) return;

    setSelectedFile(file);
    setSelectedGif(null);
  };

  const handleCommunityEmojiClick = (emojiData) => {
    setPostContent((prev) => prev + (emojiData?.emoji || ""));
    setShowEmojiPicker(false);
  };

  const handleCommunityGifSelect = (gif) => {
    const gifUrl = gif?.images?.original?.url || null;
    if (!gifUrl) return;

    setSelectedGif(gifUrl);
    setSelectedFile(null);
    setShowGifPicker(false);

    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleCommunityPostSubmit = async () => {
    if (!postContent.trim() && !selectedFile && !selectedGif) {
      alert("Post content, an image, or a GIF is required.");
      return;
    }
    if (!currentUser?.username) {
      alert("No user logged in.");
      return;
    }

    const formData = new FormData();
    formData.append("content", postContent.trim() || " Uploaded Media");
    formData.append("category", activeCategory);

    if (selectedFile) formData.append("file", selectedFile);
    if (selectedGif) formData.append("gif", selectedGif);

    try {
      const response = await fetch(`${API}/posts`, {
        method: "POST",
        credentials: "include",
        body: formData,
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || "Failed to create post.");
      }

      // Reset + refresh
      setPostContent("");
      setSelectedFile(null);
      setSelectedGif(null);
      setShowGifPicker(false);
      setShowEmojiPicker(false);
      setShowComposer(false);
      if (fileInputRef.current) fileInputRef.current.value = "";

      await fetchCategory(activeCategory);

      requestAnimationFrame(() => {
        scrollCommunitiesContentToTop();
      });
    } catch (submitErr) {
      console.error("Error creating community post:", submitErr);
      alert("Failed to create post.");
    }
  };

  const filteredPosts = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    if (!query) return posts;

    const usernameQuery = query.startsWith("@") ? query.slice(1) : query;

    return posts.filter((post) => {
      const contentMatch = String(post.content || "")
        .toLowerCase()
        .includes(query);

      const usernameMatch = String(post.username || "")
        .toLowerCase()
        .includes(usernameQuery);

      const displayNameMatch = [post.name, post.displayName].some((value) =>
        String(value || "").toLowerCase().includes(query)
      );

      return contentMatch || usernameMatch || displayNameMatch;
    });
  }, [posts, searchQuery]);

  const viewer = currentUser;

  return (
    <div className="communities-page">
      <div className="communities-header">
        <h1>Communities</h1>
        <div className="communities-subtitle">Browse by community.</div>
      </div>

      <div className="communities-selector-row">
        <div className="communities-selector-wrap">
          <button
            type="button"
            className={`communities-selector ${communityPickerOpen ? "open" : ""}`}
            onClick={() => setCommunityPickerOpen((open) => !open)}
            aria-haspopup="listbox"
            aria-expanded={communityPickerOpen}
          >
            <span className="communities-selector-copy">
              <span className="communities-selector-label">Community</span>
              <span className="communities-selector-value">{getCategoryLabel(activeCategory)}</span>
            </span>
            <span className="communities-selector-chevron" aria-hidden="true">⌄</span>
          </button>

          {communityPickerOpen && (
            <div className="communities-picker" role="listbox" aria-label="Choose community">
              {CATEGORIES.map((cat) => (
                <button
                  key={cat}
                  type="button"
                  role="option"
                  aria-selected={activeCategory === cat}
                  className={`communities-picker-option ${activeCategory === cat ? "active" : ""}`}
                  onClick={() => {
                    setActiveCategory(cat);
                    setCommunityPickerOpen(false);
                    try {
                      const url = new URL(window.location.href);
                      url.searchParams.set("category", cat);
                      window.history.replaceState({}, "", `${url.pathname}?${url.searchParams.toString()}`);
                    } catch {}
                  }}
                >
                  {getCategoryLabel(cat)}
                </button>
              ))}
              <button
                type="button"
                className="communities-picker-cancel"
                onClick={() => setCommunityPickerOpen(false)}
              >
                Cancel
              </button>
            </div>
          )}
        </div>

        <button
          type="button"
          className="community-post-btn"
          onClick={() => setShowComposer((v) => !v)}
          title={`New post in ${getCategoryLabel(activeCategory)}`}
        >
          + Post
        </button>
      </div>

      <div className="communities-search-row">
        <input
          type="search"
          className="communities-search-box"
          placeholder={`Search ${getCategoryLabel(activeCategory)} posts or @user...`}
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          aria-label={`Search ${getCategoryLabel(activeCategory)} community posts`}
        />

        {searchQuery && (
          <button
            type="button"
            className="communities-search-clear"
            onClick={() => setSearchQuery("")}
          >
            Clear
          </button>
        )}
      </div>

      <section className="community-overview" aria-live="polite">
        <h2>{getCategoryLabel(activeCategory)}</h2>
        <p>{COMMUNITY_OVERVIEWS[activeCategory]}</p>
      </section>

      {showComposer && (
        <div className="communities-newpost" style={{ marginTop: 14 }}>
          <h2 style={{ margin: "6px 0 10px 0" }}>
            New post in {getCategoryLabel(activeCategory)}
          </h2>

          <div style={{ position: "relative", width: "100%" }}>
            <textarea
              value={postContent}
              onChange={(e) => setPostContent(e.target.value)}
              placeholder="What's on your mind?"
              className="community-composer-textarea"
              style={{
                width: "100%",
                minHeight: 90,
                paddingBottom: "24px",
              }}
            />

            <div
              style={{
                position: "absolute",
                bottom: "8px",
                right: "10px",
                fontSize: "12px",
                color: "#666",
                pointerEvents: "none",
                background: "white",
              }}
            >
              {3000 - postContent.length} left
            </div>
          </div>

          {selectedFile && (
            <div
              style={{
                marginTop: "8px",
                fontSize: "0.9rem",
                display: "flex",
                alignItems: "center",
                gap: "8px",
              }}
            >
              <span>📷 {selectedFile.name}</span>
              <button
                type="button"
                onClick={() => {
                  setSelectedFile(null);
                  if (fileInputRef.current) fileInputRef.current.value = "";
                }}
              >
                Remove
              </button>
            </div>
          )}

          {selectedGif && (
            <div style={{ textAlign: "center", marginTop: "8px" }}>
              <img
                src={selectedGif}
                alt="Selected GIF"
                style={{
                  maxWidth: "80%",
                  maxHeight: "150px",
                  borderRadius: "8px",
                  marginBottom: "5px",
                }}
              />
              <button
                type="button"
                onClick={() => setSelectedGif(null)}
                style={{
                  display: "block",
                  margin: "5px auto 0",
                  cursor: "pointer",
                }}
              >
                ✖ Remove GIF
              </button>
            </div>
          )}

          <div
            style={{
              width: "100%",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: "12px",
              marginTop: "10px",
              flexWrap: "wrap",
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
              <button
                type="button"
                className="action-btn"
                title="Upload Media"
                onClick={() => fileInputRef.current?.click()}
              >
                📷
              </button>

              <button
                type="button"
                className="action-btn"
                title="Add GIF"
                onClick={() => {
                  setShowGifPicker((prev) => !prev);
                  setShowEmojiPicker(false);
                }}
              >
                🎞
              </button>

              <button
                type="button"
                className="action-btn"
                title="Insert Emoji"
                onClick={() => {
                  setShowEmojiPicker((prev) => !prev);
                  setShowGifPicker(false);
                }}
              >
                😊
              </button>

              <input
                ref={fileInputRef}
                type="file"
                accept="image/*,video/*"
                style={{ display: "none" }}
                onChange={handleCommunityMediaUpload}
              />
            </div>

            <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
              <button
                type="button"
                className="community-composer-cancel"
                title="Cancel Post"
                onClick={() => {
                  setShowComposer(false);
                  setPostContent("");
                  setSelectedGif(null);
                  setSelectedFile(null);
                  setShowGifPicker(false);
                  setShowEmojiPicker(false);
                  if (fileInputRef.current) fileInputRef.current.value = "";
                }}
              >
                ↩
              </button>

              <button
                type="button"
                className="community-composer-submit"
                onClick={handleCommunityPostSubmit}
              >
                Post
              </button>
            </div>
          </div>

          {showGifPicker && (
            <div
              style={{
                marginTop: "10px",
                padding: "12px",
                background: "white",
                border: "1px solid #ddd",
                borderRadius: "8px",
                boxShadow: "0 4px 8px rgba(0,0,0,0.12)",
              }}
            >
              <button
                type="button"
                onClick={() => setShowGifPicker(false)}
                style={{ display: "block", marginBottom: "10px", cursor: "pointer" }}
              >
                ✖ Close
              </button>

              <GiphySearchBox
                apiKey="4OQsubW6vV4uAMHoGaiK8VRRavmxEvYW"
                onSelect={handleCommunityGifSelect}
              />
            </div>
          )}

          {showEmojiPicker && (
            <div style={{ marginTop: "10px" }}>
              <EmojiPicker onEmojiClick={handleCommunityEmojiClick} />
            </div>
          )}
        </div>
      )}

      <div className="communities-content" ref={contentScrollRef}>
  {loading && (
    <div className="communities-status">
      Loading {getCategoryLabel(activeCategory)}...
    </div>
  )}

  {!loading && err && (
    <div className="communities-error">
      <b>Could not load {getCategoryLabel(activeCategory)}</b>
      <div style={{ marginTop: 6 }}>{err}</div>
    </div>
  )}

  {!loading && !err && posts.length === 0 && (
    <div className="communities-empty">
      No posts yet in <b>{getCategoryLabel(activeCategory)}</b>.
    </div>
  )}

  {!loading && !err && posts.length > 0 && filteredPosts.length === 0 && (
    <div className="communities-empty">
      No matching posts found in <b>{getCategoryLabel(activeCategory)}</b>.
    </div>
  )}

        {!loading && !err && filteredPosts.length > 0 && (
          <div className="communities-posts">
            {filteredPosts.map((post) => {
              const canEdit = canEditPost(currentUser, post);

              const viewerIsOwner = viewer?.id === post.user_id;
              const viewerTier = Number(viewer?.group_id);
              const viewerIsSubscribed = [6, 7, 8, 9].includes(viewerTier);
              const canRequestBoost =
                viewerIsOwner && viewerIsSubscribed && !post.isBoosted && !post.isAdminBoosted;

              return (
                <Post
                  key={post.id}
                  post={post}
                  currentUser={currentUser}
                  onDelete={handleDelete}
                  onLike={handleLike}
                  onReply={openReplyDialog}
                  onRepost={handleRepost}
                  onNavigateToProfile={handleProfileNavigation}
                  onShowModal={handleShowModal}
                  showCommunityLabel={false}
                  extraActions={
                    <>
                      {canRequestBoost && (
                        <button
                          className="action-btn"
                          title="Request Boost"
                          onClick={(e) => requestBoostHandler(post.id, e.currentTarget)}
                          style={{ marginRight: 6 }}
                        >
                          🚀
                        </button>
                      )}
                      {canEdit && (
                        <button
                          className="action-btn"
                          title="Edit Post"
                          onClick={() => setEditingPost(post)}
                        >
                          ✏️
                        </button>
                      )}
                    </>
                  }
                />
              );
            })}
          </div>
        )}
      </div>

      {showReplyDialog && currentReplyPost && (
        <ReplyDialog
          isOpen={showReplyDialog}
          onClose={() => setShowReplyDialog(false)}
          post={currentReplyPost}
          onSubmit={handleReply}
        />
      )}

      {editingPost && (
        <EditPostModal
          post={editingPost}
          onClose={() => setEditingPost(null)}
          onSave={(updatedPost) => {
            setPosts((prev) => prev.map((p) => (p.id === updatedPost.id ? updatedPost : p)));
            setEditingPost(null);
          }}
        />
      )}

      {showPostModal && selectedPost && (
        <PostDetailModal
          isOpen={showPostModal}
          onClose={() => setShowPostModal(false)}
          post={selectedPost}
          onLike={handleLike}
          onReply={openReplyDialog}
          onRepost={handleRepost}
        />
      )}
    </div>
  );
}